import { z } from "zod";

const requiredString = z.string().min(1);
export const schoolWrappedAppSchema = z.enum(["sidechat", "yikyak"]);

export const schoolWrappedStringFrequencySchema = z.tuple([
  requiredString,
  z.number(),
]);

export const schoolWrappedCountStatSchema = z.object({
  count: z.number(),
  rank: z.number(),
});

export const schoolWrappedAssetSchema = z.object({
  id: z.string().optional(),
  type: z.string().optional(),
  url: z.string().optional(),
  signed_url: z.string().optional(),
  content_type: z.string().optional(),
  width: z.number().optional(),
  height: z.number().optional(),
});

export const schoolWrappedGroupSchema = z.object({
  id: z.string().optional(),
  name: requiredString,
  index_name: z.string().optional(),
  analytics_name: z.string().optional(),
  membership_type: z.string().optional(),
  color: z.string().optional(),
  group_join_type: z.string().optional(),
  group_visibility: z.string().optional(),
  description: z.string().optional(),
  member_count: z.number().optional(),
  icon_url: z.string().nullable().optional(),
  asset_library_visibility: z.string().optional(),
  roles: z.array(z.unknown()).optional(),
  disable_ads: z.boolean().optional(),
  full_name: z.string().optional(),
  title: z.string().optional(),
  instagram: z.string().optional(),
  share_color_start: z.string().optional(),
  share_color_end: z.string().optional(),
  should_show_leaderboard: z.boolean().optional(),
});

const schoolWrappedCommunityGroupSchema = schoolWrappedGroupSchema.extend({
  id: requiredString,
  member_count: z.number(),
  membership_type: requiredString,
  description: z.string(),
});

export const schoolWrappedIdentitySchema = z.object({
  name: z.string().optional(),
  posted_with_username: z.boolean().optional(),
});

const optionalPostBoolean = z.preprocess(
  (value) => (value === null ? undefined : value),
  z.boolean().optional(),
);

const schoolWrappedPostBaseSchema = z.object({
  id: z.string().optional(),
  authored_by_user: z.boolean().optional(),
  alias: z.string().optional(),
  text: z.string(),
  group_id: z.string().optional(),
  group_name: z.string().optional(),
  group: schoolWrappedGroupSchema.optional(),
  vote_total: z.number(),
  comment_count: z.number(),
  created_at: requiredString,
  vote_status: z.string().optional(),
  assets: z.array(schoolWrappedAssetSchema).optional(),
  attachments: z.array(schoolWrappedAssetSchema).optional(),
  type: z.string().optional(),
  parent_post_id: z.string().optional(),
  reply_post_id: z.string().optional(),
  reply_comment_post_id: z.string().optional(),
  dms_disabled: optionalPostBoolean,
  comments_disabled: optionalPostBoolean,
  tags: z.array(z.string()).optional(),
  identity: schoolWrappedIdentitySchema.optional(),
  pinned: z.boolean().optional(),
  is_saved: z.boolean().optional(),
  follow_status: z.string().optional(),
  destination: z.string().optional(),
  index_code: z.string().optional(),
  awards: z.array(z.unknown()).optional(),
  poll_data: z.record(z.string(), z.number()).optional(),
});

export type SchoolWrappedPublicPost = z.infer<
  typeof schoolWrappedPostBaseSchema
> & {
  direct_comments?: SchoolWrappedPublicPost[];
  full_thread?: SchoolWrappedPublicPost[];
};

export const schoolWrappedPostSchema: z.ZodType<SchoolWrappedPublicPost> =
  schoolWrappedPostBaseSchema.extend({
    direct_comments: z
      .lazy(() => z.array(schoolWrappedPostSchema))
      .optional(),
    full_thread: z.lazy(() => z.array(schoolWrappedPostSchema)).optional(),
  });

const schoolWrappedPollDataSchema = z
  .record(z.string(), z.number())
  .refine((pollData) => Object.keys(pollData).length >= 2, {
    message: "Poll posts must include at least two poll options.",
  });

const optionalWrappedString = z.string().nullish();
const optionalWrappedNumber = z.number().nullish();

export const schoolWrappedPollPostSchema = schoolWrappedPostSchema.and(
  z.object({
    poll_data: schoolWrappedPollDataSchema,
  }),
);

const schoolWrappedTextPostSchema = schoolWrappedPostSchema.refine(
  (post) => post.text.trim().length > 0,
  { message: "Text posts must include text.", path: ["text"] },
);

export const schoolWrappedCommunityHighlightSchema = z.object({
  community: schoolWrappedCommunityGroupSchema,
  text_posts: z.array(schoolWrappedPostSchema).optional(),
  image_posts: z.array(schoolWrappedPostSchema).optional(),
});

const schoolWrappedPostListSchema = z.array(schoolWrappedPostSchema);
const schoolWrappedNonEmptyPostListSchema = z
  .tuple([schoolWrappedTextPostSchema])
  .rest(schoolWrappedTextPostSchema);

export const schoolWrappedPublicPayloadSchema = z.object({
  school: z.object({
    id: z.string().optional(),
    app: schoolWrappedAppSchema.nullable().optional(),
    name: requiredString,
    foreground_color: z.string().nullable().optional(),
    background_color: z.string().nullable().optional(),
    icon: z.string().nullable().optional(),
  }),
  counts: z.object({
    total_posts: schoolWrappedCountStatSchema,
    total_comments: schoolWrappedCountStatSchema,
    total_images: schoolWrappedCountStatSchema,
    total_attachment_posts: schoolWrappedCountStatSchema,
    total_chat_messages: schoolWrappedCountStatSchema,
  }),
  posts: z.object({
    top_posts: z.object({
      text: schoolWrappedNonEmptyPostListSchema,
      image: schoolWrappedPostListSchema,
    }),
    top_commented: z.object({
      text: schoolWrappedPostListSchema,
      image: schoolWrappedPostListSchema,
    }),
  }),
  community: z.object({
    most_joins: schoolWrappedCommunityHighlightSchema,
    trending: schoolWrappedCommunityHighlightSchema,
  }),
  culture: z.object({
    word_cloud: z.object({
      image_link: z.string().optional(),
      word_frequencies: z.array(schoolWrappedStringFrequencySchema).min(1),
      emoji_frequencies: z.array(schoolWrappedStringFrequencySchema).min(1),
    }),
    vibes: z.object({
      type: requiredString,
      description: requiredString,
    }),
  }),
  extras: z.object({
    polls: z.array(schoolWrappedPostSchema),
    march_yakness: z
      .object({
        seasonId: optionalWrappedString,
        roundId: optionalWrappedString,
        finalScorePoints: optionalWrappedNumber,
        uniquePlayers: optionalWrappedNumber,
        totalRuns: optionalWrappedNumber,
        leaderboardRank: optionalWrappedNumber,
        topCampusScore: optionalWrappedNumber,
        campusCupRank: optionalWrappedNumber,
        campusCupPlacement: optionalWrappedNumber,
      })
      .nullish(),
    yak_match: z
      .object({
        participantCount: optionalWrappedNumber,
        matchCount: optionalWrappedNumber,
        chatCreatedCount: optionalWrappedNumber,
        activeMatchCount: optionalWrappedNumber,
        activeMatchPercent: optionalWrappedNumber,
        distinctiveAnswer: z.record(z.string(), z.unknown()).nullish(),
        startAt: optionalWrappedString,
        endAt: optionalWrappedString,
      })
      .nullish(),
  }),
  individual: z
    .object({
      aggregation: z
        .object({
          start_at: z.string().optional(),
          end_at: z.string().optional(),
        })
        .optional(),
      posts: z
        .object({
          posts_count: z.number().optional(),
          comments_count: z.number().optional(),
          received_karma: z.number().optional(),
          top_post: schoolWrappedPostSchema.optional(),
        })
        .optional(),
      votes: z
        .object({
          upvotes_cast: z.number().optional(),
        })
        .optional(),
      games: z
        .object({
          total_games_played: z.number().optional(),
          total_plays: z.number(),
          by_game: z
            .array(
              z.object({
                game_name: z.string(),
                plays: z.number(),
                average_score: z.number().optional(),
                best_score: z.number().optional(),
              }),
            )
            .optional(),
        })
        .optional(),
      march_yakness: z
        .object({
          season_id: z.string().optional(),
          school_id: z.string().optional(),
          top_run_raw_score: z.number().optional(),
          top_run_round_id: z.string().optional(),
          top_run_completed_at: z.string().optional(),
          total_runs: z.number().optional(),
          average_raw_score: z.number().optional(),
          total_time_playing_seconds: z.number().optional(),
          rounds_played: z.number().optional(),
          season_rank_in_school: z.number().optional(),
          season_percentile_in_school: z.number().optional(),
          season_rank_global: z.number().optional(),
          season_percentile_global: z.number().optional(),
        })
        .optional(),
    })
    .nullish(),
  metadata: z
    .object({
      sidechat_url: z.string().optional(),
      yikyak_url: z.string().optional(),
      hasDeviceNotificationsEnabled: z.boolean().optional(),
    })
    .optional(),
});

export type SchoolWrappedPublicPayload = z.infer<
  typeof schoolWrappedPublicPayloadSchema
>;
